import * as React from 'react';
import {
  Chart,
  ValueAxis,
  LineSeries,
  ZoomAndPan,
  ArgumentAxis,
  Tooltip,
} from '@devexpress/dx-react-chart-material-ui';

import {
  AreaSeries,
  ArgumentScale,
  EventTracker,
  SeriesRef,
  ValueScale,
  Viewport,
} from '@devexpress/dx-react-chart';
import { purple, red } from 'globals/colors';

import { ReactElement, useState } from 'react';
import {
  amountTickFormatterForValueAxis,
  getAmountTooltipContentComponent,
  getLineSeriesComponent,
  getTooltipSheetComponent,
  scaleDateFactory,
} from 'globals/graph-utils';
import { getDateDiffInDays } from 'globals/utils';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { FsBalanceChartSingleRowDataProps } from './FsBalanceChart.fixtures';
import {
  ValueAxisGridComponent,
  ValueAxisLabelComponent,
  ArgumentAxisLabelComponent,
  TooltipOverlay,
} from './FsBalance.style-elements';
import { modifyDomain } from './FsBalanceChart.utils';
import {
  getInitialDayRange,
  getZoomLevel,
} from '../FsTrendChart/FsTrendChart.utils';

interface FsBalanceChartDataProps {
  chartData: FsBalanceChartSingleRowDataProps[];
}

const FsBalanceChart: React.FC<FsBalanceChartDataProps> = ({ chartData }) => {
  const [targetItem, setTargetItem] = useState<SeriesRef>();
  const [viewport, setViewport] = useState<Viewport>({});
  const [, setZoomLevel] = useState<number>(1);
  const initialDayRange = getInitialDayRange(chartData);
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const changeViewport = (v: Viewport): void => {
    if (v.argumentStart && v.argumentEnd) {
      const newDayRange = getDateDiffInDays(v.argumentStart, v.argumentEnd);
      const zoomLevelValue = getZoomLevel(initialDayRange, newDayRange);
      if (zoomLevelValue < 20) {
        setZoomLevel(zoomLevelValue);
        setViewport(v);
      }
    }
  };

  // TODO:
  const currency = 'USD';
  const format = 'en-US';

  const changeTargetItem = (target?: SeriesRef): void => {
    if (target && target?.series === 'ScatterSeries') return;
    setTargetItem(target);
  };

  const TooltipSheetComponent = getTooltipSheetComponent();
  const LineSeriesComponent = getLineSeriesComponent({
    strokeDasharray: 4,
  });
  const TooltipContentComponent = getAmountTooltipContentComponent({
    currency,
    format,
  });

  return (
    <Chart data={chartData} height={300}>
      {/* <p>ZOOM LEVEL: {zoomLevel}</p> */}

      {/* X */}
      <ArgumentScale name="valueScale" factory={scaleDateFactory()} />
      <ArgumentAxis
        showLine={false}
        labelComponent={ArgumentAxisLabelComponent}
      />

      {/* Y */}
      <ValueScale name="valueScale" modifyDomain={modifyDomain} />
      <ValueAxis
        scaleName="valueScale"
        labelComponent={ValueAxisLabelComponent}
        gridComponent={ValueAxisGridComponent}
        tickFormat={amountTickFormatterForValueAxis({ format, currency })}
      />

      {/* LINE BALANCE */}
      <LineSeries
        name="balance"
        valueField="balance"
        argumentField="x"
        color={purple.BRAND}
        scaleName="valueScale"
      />

      {/* AREA BALANCE */}
      <AreaSeries
        name="balanceArea"
        valueField="balance"
        argumentField="x"
        color={purple.BRAND}
        scaleName="valueScale"
      />

      {/* AREA PREDICTION */}
      <AreaSeries
        name="predictionArea"
        valueField="prediction"
        argumentField="x"
        color={red.MAIN}
        scaleName="valueScale"
      />

      {/* LINE PREDICTION */}
      <LineSeries
        name="prediction"
        valueField="prediction"
        argumentField="x"
        color={red.MAIN}
        scaleName="valueScale"
        seriesComponent={LineSeriesComponent}
      />

      {/* ZOOM AND PAN */}
      {isUpSm && (
        <ZoomAndPan viewport={viewport} onViewportChange={changeViewport} />
      )}

      {/* EVENT TRACKER */}
      <EventTracker />

      {/* TOOLTIP */}
      <Tooltip
        targetItem={targetItem}
        onTargetItemChange={changeTargetItem}
        sheetComponent={TooltipSheetComponent}
        contentComponent={TooltipContentComponent}
        overlayComponent={(props): ReactElement<Tooltip.OverlayProps> => (
          <TooltipOverlay {...props} />
        )}
      />
    </Chart>
  );
};

export default FsBalanceChart;
